import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import styles from './NotificationsPanel.module.sass';
import Icon from '../Icon';
import { getAuthToken } from '../../utils/authHelpers';
import { authFetch } from '../../utils/authFetch';

const NotificationsPanel = ({ isOpen, onClose, onNotificationRead }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isMounted, setIsMounted] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      // Trigger animation after component mounts
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
      fetchNotifications();
      // Refresh every 30 seconds when open
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    } else {
      // Start exit animation
      setIsAnimating(false);
      // Delay unmounting to allow exit animation to complete
      const timer = setTimeout(() => setIsMounted(false), 700);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        // Check if click was on the notification button (don't close)
        const notificationBtn = document.querySelector('[class*="notificationBtn"]');
        if (notificationBtn && notificationBtn.contains(event.target)) {
          return;
        }
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      if (!token) {
        setLoading(false);
        return;
      }

      const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
      const response = await authFetch(`${apiBase}/api/notifications`, {}, {
        requireAuth: false // Don't logout if no token, just skip
      });

      if (response.ok) {
        const data = await response.json();
        if (data.ok) {
          setNotifications(data.notifications || []);
          setUnreadCount(data.unreadCount || 0);
        }
      }
    } catch (error) {
      // Don't log auth errors since authFetch handles logout
      if (!error.message?.includes('Unauthorized') && !error.message?.includes('expired')) {
        console.error('Error fetching notifications:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.isRead) {
      try {
        const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
        const body = {
          notificationId: notification.id,
          notificationType: notification.type
        };

        // For batched collab events, include all underlying event IDs
        if (notification.metadata?.isBatch && notification.metadata?.eventIds) {
          body.eventIds = notification.metadata.eventIds;
        }
        // For batched collab nudges, include all underlying nudge IDs
        if (notification.metadata?.isBatch && notification.metadata?.nudgeIds) {
          body.nudgeIds = notification.metadata.nudgeIds;
        }

        await authFetch(`${apiBase}/api/notifications/mark-read`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        });

        // Update local state
        setNotifications(prev => 
          prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n)
        );

        // For batched collab events, decrease by the underlying unread event count
        let countDecrease = 1;
        if (notification.metadata?.isBatch && notification.metadata?.unreadEventCount) {
          countDecrease = notification.metadata.unreadEventCount;
        } else if (notification.metadata?.isBatch && notification.metadata?.unreadNudgeCount) {
          countDecrease = notification.metadata.unreadNudgeCount;
        }
        setUnreadCount(prev => Math.max(0, prev - countDecrease));
        
        // Notify parent to refresh badge count
        if (onNotificationRead) {
          onNotificationRead();
        }
      } catch (error) {
        // Don't log auth errors since authFetch handles logout
        if (!error.message?.includes('Unauthorized') && !error.message?.includes('expired')) {
          console.error('Error marking notification as read:', error);
        }
      }
    }

    // Navigate to the link
    onClose();
    navigate(notification.linkTo);
  };

  const handleDeleteNotification = async (event, notification) => {
    event.stopPropagation();

    try {
      const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
      const body = {
        notificationId: notification.id,
        notificationType: notification.type
      };
      if (notification.type === 'message' && notification.metadata?.messageId) {
        body.actualNotificationId = notification.metadata.messageId;
      } else if (notification.type === 'collab_nudge' && notification.metadata?.collaborationId) {
        body.actualNotificationId = notification.metadata.collaborationId;
      }

      // For batched collab events, include all underlying event IDs
      if (notification.metadata?.isBatch && notification.metadata?.eventIds) {
        body.eventIds = notification.metadata.eventIds;
      }
      // For batched collab nudges, include all underlying nudge IDs
      if (notification.metadata?.isBatch && notification.metadata?.nudgeIds) {
        body.nudgeIds = notification.metadata.nudgeIds;
      }

      await authFetch(`${apiBase}/api/notifications/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      setNotifications(prev => prev.filter(n => n.id !== notification.id));

      if (!notification.isRead) {
        let countDecrease = 1;
        if (notification.metadata?.isBatch && notification.metadata?.unreadEventCount) {
          countDecrease = notification.metadata.unreadEventCount;
        } else if (notification.metadata?.isBatch && notification.metadata?.unreadNudgeCount) {
          countDecrease = notification.metadata.unreadNudgeCount;
        }
        setUnreadCount(prev => Math.max(0, prev - countDecrease));
      }

      if (onNotificationRead) {
        onNotificationRead();
      }
    } catch (error) {
      if (!error.message?.includes('Unauthorized') && !error.message?.includes('expired')) {
        console.error('Error deleting notification:', error);
      }
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type, metadata) => {
    switch (type) {
      case 'message':
        return 'comment';
      case 'review_received':
        return 'star';
      case 'review_accepted':
        return 'check';
      case 'like_received':
        return 'heart';
      case 'favorite_received':
        return 'heart';
      case 'mention_received':
        return 'at';
      case 'comment_received':
        return 'comment';
      case 'collab_request_received':
        return 'briefcase';
      case 'collab_application_reminder':
        return 'clock';
      case 'collab_application_expired':
        return 'clock';
      case 'collab_completion_requested':
        return 'check-circle';
      case 'collab_event': {
        // Choose icon based on the specific event type
        const eventType = metadata?.eventType;
        if (eventType === 'collab_request_accepted') return 'check-circle';
        if (eventType === 'collab_request_declined') return 'close';
        if (eventType?.includes('agreement_confirmed')) return 'check';
        if (eventType?.includes('agreement_unconfirmed')) return 'close';
        if (eventType?.includes('agreement_created') || eventType?.includes('agreement_updated')) return 'edit';
        if (eventType?.includes('agreement_deleted')) return 'close';
        if (eventType?.includes('suggestion')) return 'edit';
        if (eventType?.includes('deliverable_submitted')) return 'upload';
        if (eventType?.includes('deliverable_approved')) return 'check-circle';
        if (eventType?.includes('deliverable_changes')) return 'arrow-left';
        if (eventType?.includes('deliverable_created')) return 'plus-circle';
        if (eventType?.includes('deliverable_cancelled')) return 'close';
        if (eventType?.includes('checkin')) return 'activity';
        if (eventType === 'collab_completed') return 'check-circle';
        if (eventType?.includes('review')) return 'star';
        if (eventType?.includes('accommodation')) return 'home';
        return 'briefcase';
      }
      case 'collab_nudge':
        // Return appropriate icon based on nudge type
        const nudgeType = metadata?.nudgeType;
        if (nudgeType === 'setup_pending') return 'settings';
        if (nudgeType === 'deliverable_overdue') return 'alert-triangle';
        if (nudgeType === 'stalled_collab') return 'clock';
        if (nudgeType === 'completion_ready') return 'check-circle';
        if (nudgeType === 'review_pending') return 'star';
        return 'briefcase';
      default:
        return 'bell';
    }
  };

  if (!isMounted && !isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className={styles.backdrop} 
        onClick={onClose}
        style={{ 
          opacity: isAnimating ? 1 : 0,
          pointerEvents: isAnimating ? 'all' : 'none',
          transition: 'opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      />
      
      {/* Panel */}
      <div ref={panelRef} className={cn(styles.panel, { [styles.open]: isAnimating })}>
        <div className={styles.header}>
          <h2 className={styles.title}>Notifications</h2>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
            <Icon name="close" size="20" />
          </button>
        </div>

        <div className={styles.content}>
          {loading && notifications.length === 0 ? (
            <div className={styles.emptyState}>
              <Icon name="bell" size="48" />
              <p>Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className={styles.emptyState}>
              <Icon name="bell" size="48" />
              <p>No notifications</p>
              <span className={styles.emptySubtext}>You're all caught up!</span>
            </div>
          ) : (
            <div className={styles.notificationsList}>
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={cn(styles.notificationItem, {
                    [styles.unread]: !notification.isRead
                  })}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className={cn(styles.notificationIcon, {
                    [styles.collabNudge]: notification.type === 'collab_nudge'
                  })}>
                    <Icon name={getNotificationIcon(notification.type, notification.metadata)} size="20" />
                  </div>
                  <div className={styles.notificationContent}>
                    <div className={styles.notificationHeader}>
                      <h3 className={styles.notificationTitle}>{notification.title}</h3>
                      {!notification.isRead && <div className={styles.unreadDot} />}
                    </div>
                    <p className={styles.notificationPreview}>{notification.preview}</p>
                    <span className={styles.notificationTime}>
                      {formatTimestamp(notification.timestamp)}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={styles.dismissBtn}
                    onClick={(event) => handleDeleteNotification(event, notification)}
                    aria-label="Delete notification"
                    title="Delete notification"
                  >
                    <Icon name="close" size="14" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationsPanel;

